/**
 * Document Filters tab — Stories SCH-2.4 + SCH-2.5.
 *
 * Replaces DocumentFiltersTabPlaceholder.tsx in the 5-tab Configure panel.
 *
 * Lets a Tenant Admin:
 *   - Opt in to which SubContractorHub document labels are imported (SCH-2.4).
 *   - Pick exactly one allowed label as the satellite picture source (SCH-2.4).
 *   - Map each allowed label to a Joules document category — pick from
 *     existing globals/tenant-rows or create a new tenant-scoped category
 *     inline (SCH-2.5).
 *
 * Single bulk-save endpoint for the allowlist + radio + category mapping.
 * Inline-create-category fires its own atomic endpoint (compensating delete
 * on failure), then splices the new category + filter into the discover
 * cache.
 *
 * Architecture refs:
 *   - FR26–FR31 (allowlist + satellite source + preserve-on-uncheck)
 *   - D5 (mapping table pattern)
 *   - AC1–AC10 in docs/stories/SCH-2.5-document-category-mapping.md
 */

import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Loader2,
  Plus,
  RotateCcw,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { useToast } from '@/components/ui/use-toast';

import { useTenant } from '@/contexts/TenantContext';
import {
  discoverDocumentLabelsQueryKey,
  useBulkSaveDocumentFilters,
  useCreateFilterWithNewCategory,
  useDiscoverDocumentLabels,
} from '@/hooks/integrations/useDocumentFilters';
import type {
  DocumentCategoryRow,
  DocumentFilterRow,
  NewDocumentCategoryInput,
} from '@/types/integrations/documentFilter';

interface DocumentFiltersTabProps {
  /**
   * Reports mutation pending state to the parent panel so the panel can
   * block onClose while the bulk-save mutation is in flight (AC9).
   */
  onMutationPendingChange?: (pending: boolean) => void;
  /**
   * Reports tab-level dirty state to the parent panel so the panel can
   * surface an unsaved-changes confirmation when the user attempts to
   * close while edits are pending.
   */
  onDirtyChange?: (dirty: boolean) => void;
}

interface RowDraft {
  /** Display label (original casing). */
  label: string;
  isAllowed: boolean;
  isSatellite: boolean;
  /** SCH-2.5: Joules document category mapping (BIGINT). null = un-mapped. */
  joulesDocumentCategoryId: number | null;
  /** True iff this label was NOT in existingFilters at load time. */
  isNew: boolean;
  /** True iff `isAllowed` differs from the persisted value. */
  isAllowedChanged: boolean;
  /** True iff this row was previously persisted with isAllowed=true. */
  hadPersistedAllow: boolean;
  /** Snapshot of the persisted is_satellite_picture_source value. Used to detect dirty radio state. */
  hadPersistedSatellite: boolean;
  /** SCH-2.5: snapshot of the persisted joules_document_category_id. */
  hadPersistedCategoryId: number | null;
}

// ───────────────────────────────────────────────────────────────────────────
//   Build initial draft state from the discover response
// ───────────────────────────────────────────────────────────────────────────
function buildInitialDrafts(
  discoveredLabels: string[],
  existingFilters: DocumentFilterRow[],
  /** SCH-2.5: optional auto-match suggestions to seed unmapped rows with a
   * pre-selected category id. Suggestions are keyed by lowercased label;
   * we apply them ONLY when the existing row has no persisted mapping. */
  suggestions?: Record<string, { joulesTargetId: number; confidence: number } | null>,
): Map<string, RowDraft> {
  const drafts = new Map<string, RowDraft>();

  // Index existing rows by lowercased label for case-insensitive lookup.
  const existingByLower = new Map<string, DocumentFilterRow>();
  for (const row of existingFilters) {
    existingByLower.set(row.externalLabel.trim().toLowerCase(), row);
  }

  // First pass: every discovered label gets a row (existing values when
  // available, defaults otherwise).
  for (const label of discoveredLabels) {
    const key = label.trim().toLowerCase();
    const existing = existingByLower.get(key);
    const persistedCategoryId = existing?.joulesDocumentCategoryId ?? null;
    // Auto-match: if no persisted category, pre-select the suggestion (if any).
    const suggestionId = suggestions?.[key]?.joulesTargetId ?? null;
    const seededCategoryId = persistedCategoryId ?? suggestionId;
    drafts.set(key, {
      label: existing?.externalLabel ?? label, // prefer persisted casing if it differs
      isAllowed: existing?.isAllowed ?? false,
      isSatellite: existing?.isSatellitePictureSource ?? false,
      joulesDocumentCategoryId: seededCategoryId,
      isNew: !existing,
      isAllowedChanged: false,
      hadPersistedAllow: existing?.isAllowed ?? false,
      hadPersistedSatellite: existing?.isSatellitePictureSource ?? false,
      hadPersistedCategoryId: persistedCategoryId,
    });
  }

  // Second pass: include persisted rows that are no longer in the discovery
  // sample (admin's choices are preserved even if SCH stops surfacing the
  // label on recent projects).
  for (const row of existingFilters) {
    const key = row.externalLabel.trim().toLowerCase();
    if (!drafts.has(key)) {
      drafts.set(key, {
        label: row.externalLabel,
        isAllowed: row.isAllowed,
        isSatellite: row.isSatellitePictureSource,
        joulesDocumentCategoryId: row.joulesDocumentCategoryId,
        isNew: false,
        isAllowedChanged: false,
        hadPersistedAllow: row.isAllowed,
        hadPersistedSatellite: row.isSatellitePictureSource,
        hadPersistedCategoryId: row.joulesDocumentCategoryId,
      });
    }
  }

  return drafts;
}

// ───────────────────────────────────────────────────────────────────────────
//   Main tab component
// ───────────────────────────────────────────────────────────────────────────
export function DocumentFiltersTab({
  onMutationPendingChange,
  onDirtyChange,
}: DocumentFiltersTabProps) {
  const { toast } = useToast();
  const { currentTenant } = useTenant();
  const queryClient = useQueryClient();
  const discoverQuery = useDiscoverDocumentLabels();
  const saveMutation = useBulkSaveDocumentFilters();
  const createWithCategoryMutation = useCreateFilterWithNewCategory();

  const isPending = saveMutation.isPending || createWithCategoryMutation.isPending;

  useEffect(() => {
    onMutationPendingChange?.(isPending);
  }, [isPending, onMutationPendingChange]);

  // Local draft state — keyed by lowercased label.
  const [drafts, setDrafts] = useState<Map<string, RowDraft> | null>(null);

  // Bubble tab-level dirty state up to the panel so it can guard close paths.
  useEffect(() => {
    if (!drafts) {
      onDirtyChange?.(false);
      return;
    }
    const dirty = Array.from(drafts.values()).some(
      (d) =>
        d.isAllowedChanged ||
        d.isSatellite !== d.hadPersistedSatellite ||
        d.joulesDocumentCategoryId !== d.hadPersistedCategoryId,
    );
    onDirtyChange?.(dirty);
    // Cleanup: when the tab unmounts (e.g., panel closed), clear the panel's
    // dirty flag so a stale 'true' doesn't survive into a future open.
    return () => onDirtyChange?.(false);
  }, [drafts, onDirtyChange]);
  // Track which row currently has its inline-create-category form open.
  const [inlineCreateForKey, setInlineCreateForKey] = useState<string | null>(null);

  // Initialize / re-initialize drafts when the discover query settles.
  // We merge the new server state into pending local state if any: drafts
  // for known labels keep their pending values; new server-side labels are
  // added with default state. (AC7 — re-discover doesn't lose unsaved edits.)
  useEffect(() => {
    if (!discoverQuery.data) return;
    setDrafts((prev) => {
      const fresh = buildInitialDrafts(
        discoverQuery.data!.discoveredLabels,
        discoverQuery.data!.existingFilters,
        discoverQuery.data!.suggestions,
      );
      if (!prev) return fresh;
      // Merge: keep prev entries that exist in fresh, add new fresh entries.
      const merged = new Map(fresh);
      for (const [key, prevDraft] of prev.entries()) {
        const freshDraft = merged.get(key);
        if (!freshDraft) continue;
        const isPendingEdit =
          prevDraft.isAllowedChanged ||
          prevDraft.isSatellite !== freshDraft.isSatellite ||
          prevDraft.joulesDocumentCategoryId !== freshDraft.joulesDocumentCategoryId;
        if (isPendingEdit) {
          // Preserve pending edits on labels we still know about; refresh
          // hadPersisted* baseline from the fresh server snapshot.
          merged.set(key, {
            ...prevDraft,
            hadPersistedAllow: freshDraft.hadPersistedAllow,
            hadPersistedSatellite: freshDraft.hadPersistedSatellite,
            hadPersistedCategoryId: freshDraft.hadPersistedCategoryId,
          });
        }
      }
      return merged;
    });
  }, [discoverQuery.data]);

  // ─── Loading + error states ─────────────────────────────────────────────
  if (discoverQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      </div>
    );
  }

  if (discoverQuery.isError) {
    const err = discoverQuery.error as Error & { code?: string };
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Could not load SubContractorHub document labels</AlertTitle>
        <AlertDescription>
          {err.code === 'auth_failed'
            ? 'Connection authentication failed. Rotate the API key on the Connection tab and try again.'
            : err.message || 'Try refreshing the panel.'}
        </AlertDescription>
      </Alert>
    );
  }

  if (!drafts || !discoverQuery.data) return null;

  const rowEntries = Array.from(drafts.entries()); // [key, RowDraft][]
  const hasUnsavedChanges = rowEntries.some(
    ([, d]) =>
      d.isAllowedChanged ||
      d.isSatellite !== d.hadPersistedSatellite ||
      d.joulesDocumentCategoryId !== d.hadPersistedCategoryId,
  );
  const hasAnyAllowed = rowEntries.some(([, d]) => d.isAllowed);
  const allowedCount = rowEntries.filter(([, d]) => d.isAllowed).length;
  const joulesCategories = discoverQuery.data.joulesCategories;

  // ─── Local mutators ─────────────────────────────────────────────────────
  function toggleAllowed(key: string, checked: boolean) {
    setDrafts((prev) => {
      if (!prev) return prev;
      const next = new Map(prev);
      const draft = next.get(key);
      if (!draft) return prev;
      const updated: RowDraft = {
        ...draft,
        isAllowed: checked,
        // Toggling Allow off auto-clears the Satellite flag AND clears the
        // category mapping in pending state (a category-mapped row that's
        // not allowed is meaningless — Epic 3 won't import un-allowed docs
        // even if they have a category).
        isSatellite: checked ? draft.isSatellite : false,
        joulesDocumentCategoryId: checked ? draft.joulesDocumentCategoryId : null,
        isAllowedChanged: checked !== draft.hadPersistedAllow,
      };
      next.set(key, updated);
      return next;
    });
    // Also close any open inline-create form on this row when allow is unchecked.
    if (!checked && inlineCreateForKey === key) setInlineCreateForKey(null);
  }

  function setCategoryId(key: string, categoryId: number | null) {
    setDrafts((prev) => {
      if (!prev) return prev;
      const next = new Map(prev);
      const draft = next.get(key);
      if (!draft) return prev;
      next.set(key, { ...draft, joulesDocumentCategoryId: categoryId });
      return next;
    });
  }

  function handleCreateCategoryInline(
    key: string,
    label: string,
    isSatellite: boolean,
    category: NewDocumentCategoryInput,
  ) {
    createWithCategoryMutation.mutate(
      {
        externalLabel: label,
        category,
        isSatellitePictureSource: isSatellite,
      },
      {
        onSuccess: (result) => {
          if (result.error) {
            toast({
              title: 'Could not create category',
              description: result.error.message,
              variant: 'destructive',
            });
            return;
          }
          toast({
            title: `Created '${result.data.category.displayLabel}' and mapped ${label}`,
          });
          setInlineCreateForKey(null);
          // Update the local draft to reflect the just-persisted server state.
          setDrafts((prev) => {
            if (!prev) return prev;
            const next = new Map(prev);
            const draft = next.get(key);
            if (!draft) return prev;
            next.set(key, {
              ...draft,
              isAllowed: true,
              isSatellite: result.data.filter.isSatellitePictureSource,
              joulesDocumentCategoryId: result.data.filter.joulesDocumentCategoryId,
              isNew: false,
              isAllowedChanged: false,
              hadPersistedAllow: true,
              hadPersistedSatellite: result.data.filter.isSatellitePictureSource,
              hadPersistedCategoryId: result.data.filter.joulesDocumentCategoryId,
            });
            return next;
          });
        },
      },
    );
  }

  function setSatellite(key: string) {
    setDrafts((prev) => {
      if (!prev) return prev;
      const next = new Map(prev);
      // Clear satellite flag on every other row, set it on the chosen row.
      // Radio behavior — exactly one source.
      for (const [k, draft] of next.entries()) {
        next.set(k, { ...draft, isSatellite: k === key });
      }
      return next;
    });
  }

  function clearSatellite() {
    setDrafts((prev) => {
      if (!prev) return prev;
      const next = new Map(prev);
      for (const [k, draft] of next.entries()) {
        if (draft.isSatellite) next.set(k, { ...draft, isSatellite: false });
      }
      return next;
    });
  }

  function handleRediscover() {
    queryClient.invalidateQueries({
      queryKey: discoverDocumentLabelsQueryKey(currentTenant?.tenant_id),
    });
  }

  function handleSave() {
    const rows = rowEntries.map(([, d]) => ({
      externalLabel: d.label,
      isAllowed: d.isAllowed,
      isSatellitePictureSource: d.isSatellite,
      joulesDocumentCategoryId: d.joulesDocumentCategoryId,
    }));
    saveMutation.mutate(
      { rows },
      {
        onSuccess: (result) => {
          if (result.error) {
            toast({
              title: 'Could not save document filters',
              description:
                result.error.code === 'multiple_satellite_sources'
                  ? 'Only one document can be set as the satellite picture source.'
                  : result.error.message,
              variant: 'destructive',
            });
          } else {
            toast({ title: `${rows.length} document filter${rows.length === 1 ? '' : 's'} saved` });
            // Reset draft "changed" markers — the freshly-persisted rows are
            // now the new baseline.
            setDrafts((prev) => {
              if (!prev) return prev;
              const next = new Map(prev);
              for (const [k, d] of next.entries()) {
                next.set(k, {
                  ...d,
                  isAllowedChanged: false,
                  isNew: false,
                  hadPersistedAllow: d.isAllowed,
                  hadPersistedSatellite: d.isSatellite,
                  hadPersistedCategoryId: d.joulesDocumentCategoryId,
                });
              }
              return next;
            });
          }
        },
      },
    );
  }

  // ─── Render ─────────────────────────────────────────────────────────────
  const hasAnyRows = rowEntries.length > 0;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold">Document Filters</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Choose which SubContractorHub document labels Joules should import.
            Pick one to designate as the project satellite-picture source —
            its first matching document on each project will be saved as the
            project image.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={handleRediscover}
          disabled={isPending}
        >
          <RotateCcw className="mr-1 h-3.5 w-3.5" />
          Re-discover
        </Button>
      </div>

      {!hasAnyRows && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>No document labels discovered</AlertTitle>
          <AlertDescription>
            The recent SubContractorHub project sample contained no documents.
            Add documents to a SubContractorHub project, then click Re-discover.
          </AlertDescription>
        </Alert>
      )}

      {hasAnyRows && (
        <>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div className="flex items-center gap-2">
                <CardTitle className="text-base">Labels</CardTitle>
                <Badge variant="outline">
                  {allowedCount} of {rowEntries.length} allowed
                </Badge>
              </div>
              {!hasAnyAllowed && (
                <span className="text-xs italic text-muted-foreground">
                  No labels are imported until you check at least one and save.
                </span>
              )}
            </CardHeader>
            <CardContent className="space-y-2">
              {rowEntries.map(([key, draft]) => (
                <DocumentFilterRowItem
                  key={key}
                  draft={draft}
                  joulesCategories={joulesCategories}
                  isPending={isPending}
                  isInlineCreateOpen={inlineCreateForKey === key}
                  isInlineCreatePending={
                    createWithCategoryMutation.isPending &&
                    createWithCategoryMutation.variables?.externalLabel === draft.label
                  }
                  onToggleAllow={(checked) => toggleAllowed(key, checked)}
                  onSetSatellite={() => setSatellite(key)}
                  onClearSatellite={clearSatellite}
                  onChangeCategory={(id) => setCategoryId(key, id)}
                  onOpenInlineCreate={() => setInlineCreateForKey(key)}
                  onCloseInlineCreate={() => setInlineCreateForKey(null)}
                  onSubmitInlineCreate={(category) =>
                    handleCreateCategoryInline(
                      key,
                      draft.label,
                      draft.isSatellite,
                      category,
                    )
                  }
                />
              ))}
            </CardContent>
          </Card>

          <div className="flex items-center justify-end gap-2">
            <Button
              size="sm"
              disabled={!hasUnsavedChanges || isPending}
              onClick={handleSave}
            >
              {isPending ? (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              ) : null}
              Save
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
//   Single label row
// ───────────────────────────────────────────────────────────────────────────
interface DocumentFilterRowItemProps {
  draft: RowDraft;
  joulesCategories: DocumentCategoryRow[];
  isPending: boolean;
  isInlineCreateOpen: boolean;
  isInlineCreatePending: boolean;
  onToggleAllow: (checked: boolean) => void;
  onSetSatellite: () => void;
  onClearSatellite: () => void;
  onChangeCategory: (categoryId: number | null) => void;
  onOpenInlineCreate: () => void;
  onCloseInlineCreate: () => void;
  onSubmitInlineCreate: (category: NewDocumentCategoryInput) => void;
}

function DocumentFilterRowItem({
  draft,
  joulesCategories,
  isPending,
  isInlineCreateOpen,
  isInlineCreatePending,
  onToggleAllow,
  onSetSatellite,
  onClearSatellite,
  onChangeCategory,
  onOpenInlineCreate,
  onCloseInlineCreate,
  onSubmitInlineCreate,
}: DocumentFilterRowItemProps) {
  // FR31 / AC6 inline note: when a previously-saved is_allowed=true row
  // is being toggled to false in pending state (before save).
  const showUncheckedWarning =
    !draft.isAllowed && draft.isAllowedChanged && draft.hadPersistedAllow;

  const allowCheckboxId = `allow-${useMemoStableId(draft.label)}`;

  // Category dropdown options sorted by sortOrder then displayLabel
  // (mirrors the sort the discover endpoint already returns; safe to
  // re-sort client-side for resiliency).
  const categoryOptions = useMemo(
    () =>
      [...joulesCategories]
        .filter((c) => c.isActive)
        .sort((a, b) => {
          if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
          return a.displayLabel.localeCompare(b.displayLabel);
        })
        .map((c) => ({
          value: String(c.id),
          label: `${c.icon ? c.icon + ' ' : ''}${c.displayLabel}`,
          description: c.tenantId === null ? undefined : 'Custom',
        })),
    [joulesCategories],
  );

  return (
    <div className="rounded-md border bg-card p-2">
      <div className="flex items-center gap-3 flex-wrap">
        <Checkbox
          id={allowCheckboxId}
          checked={draft.isAllowed}
          onCheckedChange={(c) => onToggleAllow(c === true)}
          disabled={isPending}
        />
        <Label htmlFor={allowCheckboxId} className="text-sm font-medium break-words flex-1 min-w-0 cursor-pointer">
          {draft.label}
        </Label>
        {draft.isNew && (
          <Badge variant="secondary" className="text-xs">
            NEW
          </Badge>
        )}
        <div className="flex items-center gap-1 shrink-0">
          {draft.isSatellite ? (
            <Button
              size="sm"
              variant="default"
              disabled={isPending}
              onClick={onClearSatellite}
            >
              ✓ Satellite source
            </Button>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              disabled={isPending || !draft.isAllowed}
              onClick={onSetSatellite}
            >
              Set as satellite source
            </Button>
          )}
        </div>
      </div>

      {/* SCH-2.5: category dropdown + "+ Create new" — visible when allowed. */}
      {draft.isAllowed && (
        <div className="mt-2 flex items-center gap-2 flex-wrap pl-6">
          <span className="text-xs text-muted-foreground shrink-0">→ Map to:</span>
          <SearchableSelect
            options={categoryOptions}
            value={
              draft.joulesDocumentCategoryId === null
                ? ''
                : String(draft.joulesDocumentCategoryId)
            }
            onValueChange={(v) => onChangeCategory(v ? Number(v) : null)}
            placeholder="Pick a Joules document category…"
            disabled={isPending}
            className="min-w-[16rem] max-w-[22rem]"
          />
          <Button
            size="sm"
            variant="outline"
            disabled={isPending}
            onClick={isInlineCreateOpen ? onCloseInlineCreate : onOpenInlineCreate}
          >
            <Plus className="mr-1 h-3.5 w-3.5" />
            {isInlineCreateOpen ? 'Cancel' : 'Create new'}
          </Button>
        </div>
      )}

      {isInlineCreateOpen && (
        <InlineCreateCategoryForm
          schLabel={draft.label}
          isPending={isInlineCreatePending}
          onSubmit={onSubmitInlineCreate}
          onCancel={onCloseInlineCreate}
        />
      )}

      {showUncheckedWarning && (
        <p className="text-xs italic text-muted-foreground mt-1 ml-6">
          Existing &lsquo;{draft.label}&rsquo; documents will remain accessible in projects
          but will not be updated.
        </p>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
//   Inline create-new-category form (SCH-2.5)
// ───────────────────────────────────────────────────────────────────────────
interface InlineCreateCategoryFormProps {
  schLabel: string;
  isPending: boolean;
  onSubmit: (category: NewDocumentCategoryInput) => void;
  onCancel: () => void;
}

/**
 * Derive a snake_case machine name from a display_label. The backend
 * pattern is /^[a-z0-9_]+$/. We don't show this to the admin — the
 * backend transparently auto-suffixes (`_2`, `_3`, ...) on collision so
 * two admins creating "Engineering Diagrams" both succeed without UX drama.
 */
function deriveMachineName(displayLabel: string): string {
  return (
    displayLabel
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'category'
  );
}

/**
 * Curated icon palette for document categories. Grouped roughly by purpose;
 * the picker renders them in this order. ~30 icons strikes a balance between
 * "enough to be useful" and "small enough to scan in one glance."
 */
const CATEGORY_ICON_PALETTE: { emoji: string; label: string }[] = [
  // Documents / paperwork
  { emoji: '📄', label: 'Document' },
  { emoji: '📋', label: 'Clipboard' },
  { emoji: '📝', label: 'Memo' },
  { emoji: '📜', label: 'Scroll' },
  { emoji: '🧾', label: 'Receipt' },
  { emoji: '📒', label: 'Ledger' },
  // Visual / media
  { emoji: '🖼️', label: 'Picture' },
  { emoji: '📸', label: 'Camera' },
  { emoji: '🎨', label: 'Design' },
  { emoji: '🛰️', label: 'Satellite' },
  // Engineering / technical
  { emoji: '📐', label: 'Engineering' },
  { emoji: '📊', label: 'Chart' },
  { emoji: '⚙️', label: 'Settings' },
  { emoji: '🔧', label: 'Tools' },
  { emoji: '🪛', label: 'Screwdriver' },
  // Solar / electrical
  { emoji: '☀️', label: 'Sun' },
  { emoji: '⚡', label: 'Electric' },
  { emoji: '🔌', label: 'Plug' },
  { emoji: '🔋', label: 'Battery' },
  // Property / location
  { emoji: '🏠', label: 'House' },
  { emoji: '🏗️', label: 'Construction' },
  // ID / legal
  { emoji: '🪪', label: 'ID Card' },
  { emoji: '✍️', label: 'Signature' },
  { emoji: '⚖️', label: 'Legal' },
  // Money / billing
  { emoji: '💰', label: 'Money' },
  { emoji: '💵', label: 'Bill' },
  // Generic
  { emoji: '📁', label: 'Folder' },
  { emoji: '🗂️', label: 'Files' },
  { emoji: '📎', label: 'Attachment' },
  { emoji: '⭐', label: 'Star' },
];

interface IconPickerProps {
  value: string;
  onChange: (emoji: string) => void;
  disabled?: boolean;
}

function IconPicker({ value, onChange, disabled }: IconPickerProps) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          className="h-9 px-3 text-base"
        >
          <span className="mr-1">{value || '🙂'}</span>
          <span className="text-xs text-muted-foreground">
            {value ? 'Change' : 'Pick icon'}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="start">
        <div className="grid grid-cols-6 gap-1">
          {CATEGORY_ICON_PALETTE.map(({ emoji, label }) => (
            <button
              key={emoji}
              type="button"
              title={label}
              aria-label={label}
              onClick={() => {
                onChange(emoji);
                setOpen(false);
              }}
              className={`h-9 w-9 rounded-md text-lg flex items-center justify-center hover:bg-accent transition-colors ${
                value === emoji ? 'bg-accent ring-1 ring-primary' : ''
              }`}
            >
              {emoji}
            </button>
          ))}
        </div>
        {value && (
          <div className="border-t mt-2 pt-2 flex justify-end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
            >
              Clear
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function InlineCreateCategoryForm({
  schLabel,
  isPending,
  onSubmit,
  onCancel,
}: InlineCreateCategoryFormProps) {
  // Pre-fill display_label with the SCH label; admin can override.
  const [displayLabel, setDisplayLabel] = useState(schLabel);
  // Default icon: a neutral document. Admin picks from the palette.
  const [icon, setIcon] = useState('📄');

  const trimmedDisplay = displayLabel.trim();
  const isFormValid = trimmedDisplay.length > 0;

  function handleSubmit() {
    onSubmit({
      // Machine name auto-derived; backend auto-suffixes on collision so
      // admins never have to think about it.
      name: deriveMachineName(trimmedDisplay),
      displayLabel: trimmedDisplay,
      icon: icon || null,
      isActive: true,
    });
  }

  return (
    <div className="ml-6 mt-2 mb-2 rounded-md border bg-muted/20 p-3 space-y-3">
      <p className="text-xs text-muted-foreground">
        Create a new Joules document category. It will be visible only to your
        tenant. After saving, this category will be linked to the
        &lsquo;{schLabel}&rsquo; SubContractorHub label.
      </p>

      <div className="space-y-3">
        <div className="space-y-1">
          <Label htmlFor="inline-cat-display" className="text-xs">
            Category name
          </Label>
          <Input
            id="inline-cat-display"
            value={displayLabel}
            onChange={(e) => setDisplayLabel(e.target.value)}
            disabled={isPending}
            placeholder="e.g., Engineering Diagrams"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Icon</Label>
          <div>
            <IconPicker value={icon} onChange={setIcon} disabled={isPending} />
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" disabled={isPending} onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" disabled={!isFormValid || isPending} onClick={handleSubmit}>
          {isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save category'}
        </Button>
      </div>
    </div>
  );
}

// Stable element id from a label string for checkbox/Label association.
function useMemoStableId(label: string): string {
  return useMemo(
    () => label.replace(/[^a-zA-Z0-9_-]/g, '-').toLowerCase(),
    [label],
  );
}
